// Lightweight wrapper around @zxing/browser. Lazy-imported so SSR never
// touches the library and the chunk only loads on tool routes.
import type { Result } from "@zxing/library";

export type DecodeResult = {
  text: string;
  format: string;
  raw: Result;
};

let readerPromise: Promise<import("@zxing/browser").BrowserMultiFormatReader> | null = null;

async function getReader() {
  if (!readerPromise) {
    readerPromise = import("@zxing/browser").then((m) => new m.BrowserMultiFormatReader());
  }
  return readerPromise;
}

export async function decodeFromImageElement(img: HTMLImageElement): Promise<DecodeResult> {
  const reader = await getReader();
  try {
    const result = await reader.decodeFromImageElement(img);
    return { text: result.getText(), format: String(result.getBarcodeFormat()), raw: result };
  } catch (err) {
    // Many real-world images (and barcodes generated by our own generator)
    // are tightly cropped with no quiet zone, which trips zxing. Retry on a
    // padded white canvas before giving up.
    const padded = padOnWhiteCanvas(img);
    if (!padded) throw err;
    const result = await reader.decodeFromCanvas(padded);
    return { text: result.getText(), format: String(result.getBarcodeFormat()), raw: result };
  }
}

/** Draw the image centred on a white canvas with a generous quiet zone. */
function padOnWhiteCanvas(img: HTMLImageElement): HTMLCanvasElement | null {
  if (typeof document === "undefined") return null;
  const w = img.naturalWidth || img.width;
  const h = img.naturalHeight || img.height;
  if (!w || !h) return null;
  // 15% of the larger side, capped — enough quiet zone for 1D symbologies.
  const pad = Math.max(20, Math.min(120, Math.round(Math.max(w, h) * 0.15)));
  const canvas = document.createElement("canvas");
  canvas.width = w + pad * 2;
  canvas.height = h + pad * 2;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, pad, pad);
  return canvas;
}

export async function decodeFromImageUrl(url: string): Promise<DecodeResult> {
  // Strategy 1: load via <img crossOrigin="anonymous"> so the canvas isn't
  // tainted. Works when the host returns Access-Control-Allow-Origin.
  try {
    const img = await loadImage(url, "anonymous");
    return await decodeFromImageElement(img);
  } catch (err) {
    // Fall through — try fetching as a blob (also CORS-gated, but some hosts
    // allow fetch even when <img crossorigin> handshake misbehaves).
    const corsHint = (err as Error)?.message?.toLowerCase?.() ?? "";
    if (!corsHint.includes("not found") && !corsHint.includes("notfound")) {
      try {
        const res = await fetch(url, { mode: "cors" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        try {
          const img = await loadImage(objectUrl);
          return await decodeFromImageElement(img);
        } finally {
          URL.revokeObjectURL(objectUrl);
        }
      } catch {
        // fall through to throw original
      }
    }
    throw err;
  }
}

function loadImage(src: string, crossOrigin?: "anonymous"): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    if (crossOrigin) img.crossOrigin = crossOrigin;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image (likely CORS or 404)"));
    img.src = src;
  });
}

export async function decodeFromFile(file: File): Promise<DecodeResult> {
  const url = URL.createObjectURL(file);
  try {
    return await decodeFromImageUrl(url);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export type CameraController = {
  stop: () => void;
};

export async function startCameraScan(
  videoEl: HTMLVideoElement,
  onResult: (r: DecodeResult) => void,
  onError?: (err: Error) => void,
): Promise<CameraController> {
  const reader = await getReader();
  void onError;
  // ZXing's `decodeFromVideoDevice` only sets `facingMode: 'environment'` with
  // no resolution hint, so browsers fall back to ~640x480 — blurry when scaled
  // to a wider video element and too low-res for reliable decoding. Request
  // 1080p as "ideal"; devices that can't deliver it downgrade gracefully.
  // The `advanced` block requests continuous autofocus — required for close-up
  // barcode scanning. `advanced` constraints are best-effort: devices that
  // don't support `focusMode` ignore the hint instead of rejecting the stream.
  const constraints: MediaStreamConstraints = {
    video: {
      facingMode: { ideal: "environment" },
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
    },
  };
  const controls = await reader.decodeFromConstraints(constraints, videoEl, (result, err) => {
    if (result) {
      onResult({
        text: result.getText(),
        format: String(result.getBarcodeFormat()),
        raw: result,
      });
      return;
    }
    // Per-frame decode failures are *expected*: ZXing fires this callback on
    // every video frame, and any frame without a clean barcode produces a
    // NotFoundException (or similar). We used to filter by `err.name`, but in
    // production builds Terser mangles the class name to a single letter, so
    // the filter never matched and onError fired on the first empty frame —
    // killing the camera the instant it opened on mobile. Just swallow.
    void err;
  });
  // Once the stream is live, re-apply focusMode directly to the track. Some
  // Android Chrome builds ignore `focusMode` inside the initial constraints
  // but honour it via `applyConstraints()` after the track is running.
  void applyContinuousAutofocus(videoEl);
  return { stop: () => controls.stop() };
}

async function applyContinuousAutofocus(videoEl: HTMLVideoElement): Promise<void> {
  const stream = videoEl.srcObject;
  if (!(stream instanceof MediaStream)) return;
  const track = stream.getVideoTracks()[0];
  if (!track) return;
  try {
    await track.applyConstraints({
      advanced: [{ focusMode: "continuous" } as MediaTrackConstraintSet],
    });
  } catch {
    // Track doesn't support focusMode (e.g. Safari, some webcams) — leave the
    // device's default behaviour alone.
  }
}

export async function listVideoInputDevices() {
  const mod = await import("@zxing/browser");
  return mod.BrowserMultiFormatReader.listVideoInputDevices();
}

/** Map zxing's numeric BarcodeFormat enum value to a friendly label. */
const FORMAT_NAMES: Record<string, string> = {
  "0": "Aztec",
  "1": "Codabar",
  "2": "Code 39",
  "3": "Code 93",
  "4": "Code 128",
  "5": "Data Matrix",
  "6": "EAN-8",
  "7": "EAN-13",
  "8": "ITF",
  "9": "MaxiCode",
  "10": "PDF417",
  "11": "QR Code",
  "12": "RSS-14",
  "13": "RSS Expanded",
  "14": "UPC-A",
  "15": "UPC-E",
  "16": "UPC/EAN Extension",
};

export function formatLabel(format: string) {
  return FORMAT_NAMES[format] ?? format;
}
